import React, { useState, useMemo, useEffect, useRef, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import './CollapsibleMessage.css';

/**
 * 🔧 模块级别状态缓存 - 解决虚拟列表卸载/重新挂载时状态丢失问题
 */
const messageExpandedCache = new Map();

/**
 * 🔧 常量默认值
 */
const NOOP = () => {};

const CollapsibleMessage = memo(({ 
  content, 
  messageId = null,  // 🔧 新增：消息唯一标识
  initialExpanded = false, 
  onToggle = NOOP  // 🔧 使用常量默认值
}) => {
  const { t } = useTranslation();
  // 🔧 从缓存读取状态
  const [isExpanded, setIsExpanded] = useState(() => {
    if (messageId && messageExpandedCache.has(messageId)) {
      return messageExpandedCache.get(messageId);
    }
    return initialExpanded;
  });
  const initialRenderRef = useRef(true);
  
  // 🔧 状态变化时更新缓存 + 通知父组件
  useEffect(() => {
    if (messageId) {
      messageExpandedCache.set(messageId, isExpanded);
    }
    // 跳过初始渲染
    if (!initialRenderRef.current && onToggle !== NOOP) {
      onToggle(isExpanded);
    }
  }, [messageId, isExpanded, onToggle]);

  const processedContent = useMemo(() => {
    // 如果已经是字符串，直接返回
    if (typeof content === 'string') {
      return content;
    }

    // 如果是 React 元素
    if (React.isValidElement(content)) {
      // 尝试获取文本内容
      const extractText = (element) => {
        if (typeof element === 'string') return element;
        if (React.isValidElement(element)) {
          return React.Children.map(element.props.children, child => 
            extractText(child)
          )?.join('') || '';
        }
        return '';
      };

      return extractText(content);
    }

    // 对象转字符串
    return JSON.stringify(content, null, 2);
  }, [content]);

  const previewContent = useMemo(() => {
    const lines = processedContent.split('\n');
    // 显示前4行（而不是3行）
    return lines.slice(0, 4).join('\n') + (lines.length > 4 ? '...' : '');
  }, [processedContent]);

  // 🔧 直接派生折叠状态，避免额外的 state 更新导致抖动
  const needsExpansion = useMemo(() => {
    const lines = processedContent.split('\n').length;
    return lines > 15;
  }, [processedContent]);
  
  // 处理初始展开状态
  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      if (initialExpanded) {
        setIsExpanded(true);
      }
    }
  }, [initialExpanded]);
  
  // 🔧 使用 useCallback 稳定函数引用
  const handleToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  return (
    <div className="collapsible-message">
      <div
        className={`message-content ${isExpanded ? 'expanded' : 'collapsed'}`}
        data-expanded={isExpanded}
      >
        {isExpanded ? content : (needsExpansion ? previewContent : content)}
      </div>
      {needsExpansion && (
        <button 
          className="expand-button"
          onClick={handleToggle}
        >
          {isExpanded ? t('collapsible_collapse') : t('collapsible_expand')}
        </button>
      )}
    </div>
  );
});

export default CollapsibleMessage;